import React, { ChangeEvent, Component } from 'react';
import { Link } from 'react-router-dom';
import GearDTO from '../../../../common/api/dtos/Gear';
import { listGears } from '../../../../common/api/endpoints/gears';
import { getFilteredItems } from '../../../../common/helpers/Filter';
import errorImage from '../../../../common/helpers/ImageError';
import { ChangeSortBy } from '../../../../common/helpers/Sorting';
import TableSort from '../../../../common/helpers/TableSort';
import ToolbarControls from '../../../generics/Header/ToolbarControls';
import { TRequestStatus } from '../../../../common/types/RequestStatus';
import ToggleControl from '../../../controls/ToggleControl/ToggleControl';
import GhostButtonControl from '../../../controls/GhostButtonControl/GhostButtonControl';
import TextControl from '../../../controls/TextControl/TextControl';
import RequestStatus from '../../../utils/RequestStatus/RequestStatus';
import SortButton from '../../../utils/SortButton/SortButton';
import BreadcrumbControls from '../../../generics/Header/BreadcrumbControls';
import DateTCell from '../../../utils/DateTCell/DateTCell';
import Thumbnail from '../../../utils/Thumbnail/Thumbnail';
import { withTransitionEvent } from '../../../TransitionEvent';
import VirtualTableRow from '../../../utils/Table/VirtualTableRow';

interface State {
  items?: GearDTO[],
  tableSort: TableSort,
  status: TRequestStatus,
  showDeleted: boolean,
  showSensitive: boolean,
  filterValue: string,
}

class ItemList extends Component<{}, State> {
  availableFilters: string[] = [
    'name',
    'internalId',
    'accountingId',
    'description',
    'comments',
    'computer.name',
    'lastUpdate',
    'inboundDate',
    'outboundDate',
    'user.name',
    'user.fullName',
  ]

  constructor(props: {}) {
    super(props);
    this.state = {
      status: 'loading',
      filterValue: '',
      tableSort: new TableSort('name', 'asc'),
      showSensitive: false,
      showDeleted: localStorage.getItem('showDeleted') === 'false' || localStorage.getItem('showDeleted') === null ? false : true,
    };
  }

  sensitiveTimeout: number = 0;

  fetchItems = async () => {
    this.setState({
      status: 'loading'
    });

    const items = await listGears();

    this.setState({
      status: 'success',
      items: items,
    });
  }

  
  toggleSensitive = (event: ChangeEvent<HTMLInputElement>) => {
    this.state.showSensitive ? this.setState({showSensitive: false}) : this.setState({showSensitive: true});
  }

  showDeleted = (event: ChangeEvent<HTMLInputElement>) => {
    localStorage.setItem(event.target.name, JSON.stringify(event.target.checked));
    this.state.showDeleted ? this.setState({ showDeleted: false }) : this.setState({ showDeleted: true });
  }

  handleSortChange = (column: string) => {
    this.setState((prevState: State) => {
      return {
        tableSort: ChangeSortBy(column, prevState.tableSort.sortBy, prevState.tableSort.sortDirection)
      };
    });
  }

  setFilterValue = (ev: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filterValue: ev.target.value,
    })
  }

  componentDidMount() {
    this.fetchItems();
  }

  componentDidUpdate(prevState: State) {
    if(this.state.showSensitive !== prevState.showSensitive) {
      if(this.state.showSensitive){
        this.sensitiveTimeout = setTimeout(() => {
          this.setState({showSensitive: false});
        }, parseInt(String(process.env.REACT_APP_SENSITIVE_DATA_TIMEOUT))) as unknown as number;
      } else {
        clearTimeout(this.sensitiveTimeout);
      }
    }
  }
  
  renderTHeadRow() {
    return (
      <tr>
        <th>#</th>
        <th>
          <SortButton
            column='name'
            text='Name'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='internalId'
            text='Internal ID'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='accountingId'
            text='Accounting ID'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='inboundDate'
            text='Inbound'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='outboundDate'
            text='Outbound'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='description'
            text='Description'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='comments'
            text='Comments'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='computer.name'
            text='Computer'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='user.fullName'
            text='Employee'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th>
          <SortButton
            column='lastUpdate'
            text='Last update'
            tableSort={this.state.tableSort}
            onClick={this.handleSortChange}
          ></SortButton>
        </th>
        <th></th>
      </tr>
    );
  }

  renderTBodyRow(item: GearDTO) {
    return (
      <VirtualTableRow
        id={item.id}
        key={item.id}
        rowHeight={65}
        numberOfPrerenders={15}
      >
        <td></td>
        <td>
          <div className="flex-row fill">
            <div className="column flex-v-center">
              <Thumbnail
                thumbnailURL={process.env.REACT_APP_INVENTORY_PATH + item.iconUrl}
                thumbnailBackground={item.tileColor}
                classes="padded"
              />
              <div>
                <span>{item.name}</span>
              </div>
            </div>
          </div>
        </td>
        <td>
          <span>{item?.internalId}</span>
        </td>
        <td>
          <span>{item?.accountingId}</span>
        </td>
        <DateTCell date={item.inboundDate} />
        <DateTCell date={item.outboundDate} />
        <td>
          <p className="text-chunk">{item.description}</p>
        </td>
        <td>
          <p className="text-chunk">{item.comments}</p>
        </td>
        <td>
          <span>{item.computer?.name}</span>
        </td>
        <td>
          <span>{item.user?.name}</span><br />
          <span className={!this.state.showSensitive ? 'blurred-item' : 'blurred-item deblur'}>{item.user?.fullName}</span>
        </td>
        <DateTCell date={item.lastUpdate} />
        <td className="text-right">
          <GhostButtonControl to={`/item/edit/${item.id}`} class="ghost-button" icon="fas fa-angle-right"></GhostButtonControl>
        </td>
      </VirtualTableRow>
    );
  }

  render() {
    const filteredItems = getFilteredItems(this.state.filterValue, this.availableFilters, this.state.items ? this.state.items : []).sort(this.state.tableSort.sortByColumn).filter((item: GearDTO) => item.deleted === this.state.showDeleted);
    return (
      <>
        <BreadcrumbControls
          pageTitle="Items"
          status={this.state.status}
        />
        {this.state.items?.length && 
          <div>
            <ToolbarControls>
              <TextControl
                label="Filter"
                placeholder="Filter"
                type="text"
                name="filterBox"
                id="filterBox"
                onChange={this.setFilterValue}
                srOnly={true}
              />
              <ToggleControl
                id="showSensitive"
                name="showSensitive"
                changeMethod={this.toggleSensitive}
                isChecked={this.state.showSensitive}
                labelText="Sensitive data"
                icon="fas fa-shield-alt"
                srOnly={true}
                title="Sensitive data"
              />
              <ToggleControl
                id="showDeleted"
                name="showDeleted"
                changeMethod={this.showDeleted}
                isChecked={this.state.showDeleted}
                labelText="Recycle bin"
              icon="fas fa-trash-undo"
              srOnly={true}
              title="Recyle bin"
              />
              <div className="form-group">
                <Link to={'/item/add'} className="primary-button"><span className="static-icon"><span className="fas fa-plus-circle"></span></span> <span className="text">Add item</span></Link>
              </div>
            </ToolbarControls>

            <div className="flex-row fill">
              <div className="column">
                <div className="tableview-component">
                    <div className={`
                        card
                        ${!this.state.items ? 'loader-border' : ''}
                        ${this.state.showDeleted ? "flashing-border" : ""}
                    `}>
                    <table>
                      <thead>
                        {this.renderTHeadRow()}
                      </thead>
                      {this.state.items &&
                        <React.Fragment>
                          <tbody>
                            {filteredItems.map(item => (
                              this.renderTBodyRow(item)
                            ))}
                          </tbody>
                          <tfoot>
                            {this.state.items?.length === 0 &&
                              <tr>
                                <th colSpan={12}>
                                  <div className="card">
                                    <p className="text-chunk">There are no items defined. Start by <Link className="link-button" to="/item/add">adding</Link> the first one.</p>
                                  </div>
                                </th>
                              </tr>
                            }
                            {this.state.filterValue &&
                              <tr>
                                <th colSpan={12}>
                                  <div className="card">
                                    <p className="text-chunk">Showing {filteredItems.length} out of {this.state.items.filter((item: GearDTO) => item.deleted === this.state.showDeleted).length} results.</p>
                                  </div>
                                </th>
                              </tr>
                            }
                          </tfoot>
                        </React.Fragment>
                      }
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </>
    );
  }
}

export default withTransitionEvent(ItemList);
